import { useEffect, useState } from 'react'
import { HelpCircle, Settings2 } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { networksApi } from '@/api/networksApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { usePermission } from '@/lib/permissions'
import {
  DEFAULT_IDLE_MINUTES,
  IDLE_PRESET_MINUTES,
  IDLE_UNITS,
  MAX_IDLE_MINUTES,
  MIN_IDLE_MINUTES,
  formatIdleDuration,
  splitDuration,
  toMinutes,
  type IdleUnit,
} from '@/lib/idleClock'

/**
 * Instance-wide settings — Configure → General.
 *
 * Holds the session idle timeout. It is instance-wide rather than per tenant
 * because a session is not tenant-scoped: the token is global to the user and
 * tenants are switched inside one session, so a per-tenant value would have no
 * coherent answer for someone who belongs to two.
 *
 * Gated on `isSuperAdmin`, not `can(...)`: `PATCH /networks/current` is a global
 * route with no tenant guard, so a tenant role holding `tenants.manage` would
 * render a control whose every request 403s. Everyone else keeps the empty
 * state, which is what satisfies "a non-administrator can neither view nor
 * change it" without hiding the tab itself — General is the default tab and the
 * target of `?tab=general` links, so gating the trigger would move where
 * Configure opens for every non-superadmin.
 */

/** Stores null: "not configured", which follows whatever the default becomes. */
const USE_DEFAULT = 'default'
/** Reveals the number + unit fields. Never sent as a value. */
const CUSTOM = 'custom'

export function GeneralSettings() {
  const { isSuperAdmin } = usePermission()
  const queryClient = useQueryClient()

  const { data: network, isLoading } = useQuery({
    queryKey: ['instance-network'],
    queryFn: () => networksApi.current(),
    enabled: isSuperAdmin,
  })

  const [selected, setSelected] = useState<string>(USE_DEFAULT)
  const [customValue, setCustomValue] = useState('')
  const [customUnit, setCustomUnit] = useState<IdleUnit>('minutes')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const stored = network?.idleTimeoutMinutes ?? null

  useEffect(() => {
    if (!network) return
    if (stored === null) {
      setSelected(USE_DEFAULT)
      return
    }
    // A stored value that is not on the ladder opens in the custom fields,
    // expressed in the largest unit that divides it exactly — the number the
    // admin is likely to have typed, not the minutes the column happens to
    // hold. Snapping it to the nearest preset would silently change a setting
    // nobody asked to change.
    if ((IDLE_PRESET_MINUTES as readonly number[]).includes(stored)) {
      setSelected(String(stored))
      return
    }
    const { value, unit } = splitDuration(stored)
    setSelected(CUSTOM)
    setCustomValue(String(value))
    setCustomUnit(unit)
  }, [network, stored])

  const save = useMutation({
    mutationFn: (minutes: number | null) => networksApi.update({ idleTimeoutMinutes: minutes }),
    onSuccess: async (updated) => {
      // Write through so the idle clock, which reads this same query, picks the
      // new value up without a reload.
      queryClient.setQueryData(['instance-network'], updated)
      await queryClient.invalidateQueries({ queryKey: ['instance-network'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Could not save the session timeout')
    },
  })

  if (!isSuperAdmin) {
    return (
      <div className='max-w-xl rounded-lg border border-dashed border-gray-300 p-6 text-center'>
        <Settings2 className='mx-auto h-5 w-5 text-gray-400' />
        <p className='mt-2 text-sm font-medium text-gray-700'>No settings available</p>
      </div>
    )
  }

  function handleSave() {
    setError(null)

    if (selected === USE_DEFAULT) {
      save.mutate(null)
      return
    }

    if (selected !== CUSTOM) {
      save.mutate(Number(selected))
      return
    }

    // Validated here as well as server-side so the message lands next to the
    // field rather than arriving as a failed request.
    const raw = customValue.trim()
    const amount = Number(raw)
    if (raw === '' || !Number.isInteger(amount) || amount <= 0) {
      setError('Enter a whole number greater than zero.')
      return
    }

    const minutes = toMinutes(amount, customUnit)
    if (minutes < MIN_IDLE_MINUTES || minutes > MAX_IDLE_MINUTES) {
      setError(`Choose a period between ${formatIdleDuration(MIN_IDLE_MINUTES)} and ${formatIdleDuration(MAX_IDLE_MINUTES)}.`)
      return
    }

    save.mutate(minutes)
  }

  return (
    <div className='max-w-xl space-y-2'>
      <div className='flex items-center gap-1.5'>
        <Label htmlFor='idle-timeout'>Session timeout</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type='button' aria-label='About the session timeout'>
                <HelpCircle className='h-3.5 w-3.5 shrink-0 cursor-help text-muted-foreground' />
              </button>
            </TooltipTrigger>
            <TooltipContent side='right' className='max-w-xs'>
              How long someone can be inactive before they are signed out. They are warned first and
              can stay signed in. A change applies the next time each session loads.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        <Select
          value={selected}
          disabled={isLoading || save.isPending}
          onValueChange={(v) => {
            setSelected(v)
            setError(null)
          }}
        >
          <SelectTrigger id='idle-timeout' className='w-56'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={USE_DEFAULT}>
              Default ({formatIdleDuration(DEFAULT_IDLE_MINUTES)})
            </SelectItem>
            {IDLE_PRESET_MINUTES.map((m) => (
              <SelectItem key={m} value={String(m)}>
                {formatIdleDuration(m)}
              </SelectItem>
            ))}
            <SelectItem value={CUSTOM}>Custom…</SelectItem>
          </SelectContent>
        </Select>

        {selected === CUSTOM && (
          <>
            <Input
              type='number'
              inputMode='numeric'
              min={1}
              aria-label='Custom timeout amount'
              value={customValue}
              disabled={save.isPending}
              onChange={(e) => {
                setCustomValue(e.target.value)
                setError(null)
              }}
              className='w-24'
              aria-invalid={error ? true : undefined}
            />
            <Select
              value={customUnit}
              disabled={save.isPending}
              onValueChange={(v) => {
                setCustomUnit(v as IdleUnit)
                setError(null)
              }}
            >
              <SelectTrigger className='w-32' aria-label='Custom timeout unit'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {IDLE_UNITS.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.id.charAt(0).toUpperCase() + u.id.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}

        <Button onClick={handleSave} disabled={isLoading || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>

        {saved && (
          <span role='status' className='text-sm text-green-700'>
            Saved
          </span>
        )}
      </div>

      {error && (
        <p role='alert' className='text-xs font-medium text-red-600'>
          {error}
        </p>
      )}
    </div>
  )
}
